import { Component, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { Router, RouterModule } from "@angular/router";
import { FormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { forkJoin, of } from "rxjs";
import { catchError, finalize, map, switchMap } from "rxjs/operators";
import { HorarioLaboral } from "src/app/shared/model/tarea/horario-laboral.model";
import { HorarioLaboralService } from "../horario-laboral.service";
import { etiquetaTolerancia } from "../horario-editor/tolerancia-horario";

@Component({
  selector: "app-gestion-horarios",
  standalone: true,
  templateUrl: "./gestion-horarios.component.html",
  styleUrls: ["./gestion-horarios.component.css"],
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatIconModule,
  ],
})
export class GestionHorariosComponent implements OnInit {
  horarios: HorarioLaboral[] = [];
  loading = false;
  filtroNombre = "";
  filtroEstatus = "ACTIVO";

  constructor(
    private router: Router,
    private horarioService: HorarioLaboralService
  ) {}

  ngOnInit(): void {
    this.consultarHorarios();
  }

  consultarHorarios(): void {
    const filtro = new HorarioLaboral();
    filtro.nombre = this.filtroNombre?.trim() || null;
    filtro.estatus = this.filtroEstatus || null;

    this.loading = true;
    this.horarioService
      .consultarHorarios(filtro)
      .pipe(
        switchMap((response) => this.hidratarHorarios(response?.list ?? [])),
        finalize(() => (this.loading = false))
      )
      .subscribe({
        next: (horarios) => {
          this.horarios = horarios;
        },
        error: () => {
          this.horarios = [];
        },
      });
  }

  clearFilters(): void {
    this.filtroNombre = "";
    this.filtroEstatus = "ACTIVO";
    this.consultarHorarios();
  }

  nuevoHorario(): void {
    this.router.navigate(["/TAREA/horarios/alta"]);
  }

  editarHorario(horario: HorarioLaboral): void {
    this.router.navigate(["/TAREA/horarios/edicion", horario.id]);
  }

  irAsignacion(): void {
    this.router.navigate(["/TAREA/horarios/asignacion"]);
  }

  getDiasConfigurados(horario: HorarioLaboral): string {
    if (!horario?.dias?.length) {
      return "Sin días configurados";
    }

    if (horario.dias.length <= 3) {
      return horario.dias
        .map((dia) => this.getDayLabel(dia.diaSemana))
        .join(", ");
    }

    const dias = horario.dias.map((dia) => this.getDayLabel(dia.diaSemana));
    return `${dias.slice(0, 3).join(", ")} +${dias.length - 3}`;
  }

  /**
   * La tolerancia propia del horario, o que aplica la de Parámetros. Se
   * muestra porque ahora se captura en los dos editores y, sin verla aquí,
   * no hay forma de saber cuál de las dos califica los retardos.
   */
  getTolerancia(horario: HorarioLaboral): string {
    return etiquetaTolerancia(horario?.minutosTolerancia);
  }

  getStatusClass(estatus: string): string {
    return estatus === "ACTIVO"
      ? "table-page__status-badge is-active"
      : "table-page__status-badge is-inactive";
  }

  private getDayLabel(day: number): string {
    const labels: Record<number, string> = {
      1: "Lun",
      2: "Mar",
      3: "Mié",
      4: "Jue",
      5: "Vie",
      6: "Sáb",
      7: "Dom",
    };
    return labels[day] ?? `Día ${day}`;
  }

  private hidratarHorarios(horarios: HorarioLaboral[]) {
    if (!horarios.length) {
      return of([]);
    }

    return forkJoin(
      horarios.map((horario) => {
        if (!horario?.id) {
          return of(horario);
        }

        return this.horarioService.obtenerHorario(horario.id).pipe(
          map((detalle) => ({
            ...horario,
            dias: detalle?.dias ?? [],
          })),
          catchError(() => of(horario))
        );
      })
    );
  }
}
